import type { ReactNode } from 'react';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import {
  FieldOption,
  GetMembersRow,
  MemberDefaultFieldOrder,
} from '@/components/publicSearch/constants';
import { OptionsByField } from '@/components/publicSearch/search/constants';
import { getGridOperatorsByField } from '@/components/publicSearch/search/helpers';
import { createRenderCell } from './helpers';
import type { GetMembersQuery } from '@/publicSearch/graphql/queries.generated';

export type MemberListFieldConfigItem = {
  field: string;
  label: string;
  display: number;
  order: string;
};

type SettingsArray =
  | Array<{ societySettingType?: string | null; value?: string | null }>
  | null
  | undefined;

export function parseMemberListFieldsConfig(
  settings: SettingsArray,
): MemberListFieldConfigItem[] {
  if (!settings || !Array.isArray(settings)) return [];
  const setting = settings.find(
    (s) =>
      s && s.societySettingType === SOCIETY_SETTING_TYPE.MEMBER_LIST_FIELDS,
  );
  if (!setting?.value) return [];
  try {
    const parsed = JSON.parse(setting.value) as MemberListFieldConfigItem[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && item.display === 1 && item.field)
      .sort(
        (a, b) => parseInt(String(a.order), 10) - parseInt(String(b.order), 10),
      );
  } catch {
    return [];
  }
}

export function getVisibleMemberFieldKeys(settings: SettingsArray): string[] {
  return parseMemberListFieldsConfig(settings).map((item) =>
    mapMemberSettingFieldToRowKey(item.field),
  );
}

function mapMemberSettingFieldToRowKey(field: string): string {
  const socMemberMatch = field.match(/^SocietyMemberField(\d+)$/i);
  if (socMemberMatch) {
    return `SOCIETY_MEMBER_FIELD_${socMemberMatch[1]}`;
  }
  const known: Record<string, string> = {
    MemberId: 'MEMBER_ID',
    MemberIdent: 'MEMBER_IDENT',
    MembName: 'MEMB_NAME',
    MemberName: 'MEMB_NAME',
    Prefix: 'PREFIX',
    MemberType: 'MEMBER_TYPE',
    MembershipType: 'MEMBER_TYPE',
    Postcode: 'POSTCODE',
    Address1: 'ADDRESS1',
    Address2: 'ADDRESS2',
    Address3: 'ADDRESS3',
    Sortname: 'SORTNAME',
    Email: 'EMAIL',
    PhoneDetails: 'PHONE_DETAILS',
    Homepage: 'HOMEPAGE',
    HerdSize: 'HERD_SIZE',
    MembStatus: 'MEMB_STATUS',
    MemberStatus: 'MEMB_STATUS',
    MapDetails: 'MAP_DETAILS',
  };
  return (
    known[field] ||
    field
      .replace(/([A-Z])/g, '_$1')
      .replace(/^_/, '')
      .toUpperCase()
  );
}

const LINKABLE_FIELDS = new Set([
  FieldOption.member.value,
  FieldOption.memberName.value,
  FieldOption.memberIdent.value,
]);

function buildMemberColumnFromConfig(
  configItem: MemberListFieldConfigItem,
  orgCode: string,
): GridColDef<GetMembersRow> | null {
  if (!configItem.field || typeof configItem.field !== 'string') {
    return null;
  }
  const fieldKey = mapMemberSettingFieldToRowKey(configItem.field);
  const label = (configItem.label || '').replace(/\^/g, ' ');
  const renderCell = createRenderCell(fieldKey as keyof GetMembersRow, orgCode);

  return {
    field: fieldKey,
    headerClassName: LINKABLE_FIELDS.has(fieldKey) ? 'pl' : '',
    headerName: label,
    sortable: true,
    flex: 1,
    minWidth: 120,
    filterOperators: getGridOperatorsByField(fieldKey),
    type: fieldKey in OptionsByField ? 'singleSelect' : undefined,
    valueOptions:
      fieldKey in OptionsByField
        ? () => OptionsByField[fieldKey]!.map(({ value }) => value)
        : undefined,
    renderCell: (params: GridRenderCellParams<GetMembersRow>) =>
      renderCell(params) as ReactNode,
  } as GridColDef<GetMembersRow>;
}

export function buildMemberColumns(
  config: MemberListFieldConfigItem[],
  orgCode: string,
): GridColDef<GetMembersRow>[] {
  if (config.length > 0) {
    return config
      .map((item) => buildMemberColumnFromConfig(item, orgCode))
      .filter((col): col is GridColDef<GetMembersRow> => col !== null);
  }

  return MemberDefaultFieldOrder.map(({ label, value }) => {
    const renderCell = createRenderCell(value as keyof GetMembersRow, orgCode);
    return {
      field: value,
      headerClassName: LINKABLE_FIELDS.has(value) ? 'pl' : '',
      headerName: label,
      sortable: true,
      flex: 1,
      minWidth: 120,
      filterOperators: getGridOperatorsByField(value),
      type: value in OptionsByField ? 'singleSelect' : undefined,
      valueOptions:
        value in OptionsByField
          ? () => OptionsByField[value]!.map(({ value: v }) => v)
          : undefined,
      renderCell: (params: GridRenderCellParams<GetMembersRow>) =>
        renderCell(params) as ReactNode,
    } as GridColDef<GetMembersRow>;
  });
}

type MemberItem = GetMembersQuery['members']['items'][number];

const SKIP_KEYS = new Set(['__typename', 'documents']);

export function buildMemberRows(
  items: MemberItem[] | undefined,
): GetMembersRow[] {
  if (!items) return [];

  return items.map((item) => {
    const row: Record<string, unknown> = {};
    const itemAny = item as Record<string, unknown>;

    // Composite display field
    row[FieldOption.member.value] =
      item.memberIdent && item.memberName
        ? `${item.memberIdent} - ${item.memberName}`
        : item.memberName || item.memberIdent || '';

    // Preserve documents array for gallery image display (kept out of generic mapping)
    if (Array.isArray(item.documents)) {
      row['documents'] = item.documents;
    }

    // Dynamically map every GraphQL field → UPPER_CASE row key
    for (const key of Object.keys(itemAny)) {
      if (SKIP_KEYS.has(key)) continue;
      const val = itemAny[key];
      if (val == null) continue;

      // societyMemberField1-15 → SOCIETY_MEMBER_FIELD_N
      const socMatch = key.match(/^societyMemberField(\d+)$/);
      if (socMatch) {
        row[`SOCIETY_MEMBER_FIELD_${socMatch[1]}`] = val;
        continue;
      }

      // Use the same mapping function that columns use
      const ucKey = key.charAt(0).toUpperCase() + key.slice(1);
      row[mapMemberSettingFieldToRowKey(ucKey)] = val;
    }

    return row as unknown as GetMembersRow;
  });
}
